'use client'

import { useLayoutEffect, useState } from 'react'
import { Link } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import type { SupportCategory } from '@/lib/support/docs'
import type { SupportCategoryId } from '@/lib/support/categories'

interface SupportSidebarProps {
  categories: SupportCategory[]
  activeCategory?: SupportCategoryId
  activeSlug?: string
}

const DESKTOP_QUERY = '(min-width: 1024px)'

export default function SupportSidebar ({
  categories,
  activeCategory,
  activeSlug
}: SupportSidebarProps) {
  const t = useTranslations('Support')
  const [open, setOpen] = useState<string[]>(() => categories.map((category) => category.id))

  useLayoutEffect(() => {
    const ids = categories.map((category) => category.id)
    const media = window.matchMedia(DESKTOP_QUERY)
    const apply = () => {
      setOpen(media.matches ? ids : (activeCategory ? [activeCategory] : []))
    }
    apply()
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [categories, activeCategory])

  const toggle = (id: string) => {
    setOpen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  return (
    <aside className='support-sidebar' aria-label={t('docsLabel')}>
      <nav className='support-sidebar__chips' aria-label={t('docsLabel')}>
        {categories.map((category) => {
          const isActive = category.id === activeCategory
          return (
            <Link
              key={category.id}
              href={`/support/${category.id}`}
              className={isActive ? 'is-active' : undefined}
              aria-current={isActive ? 'page' : undefined}
            >
              {category.title}
            </Link>
          )
        })}
      </nav>
      <nav className='support-sidebar__nav'>
        {categories.map((category) => {
          const isOpen = open.includes(category.id)
          return (
            <div key={category.id} className={`support-sidebar__group${isOpen ? ' is-open' : ''}`}>
              <button
                type='button'
                className='support-sidebar__group-toggle'
                aria-expanded={isOpen}
                onClick={() => toggle(category.id)}
              >
                {category.title}
              </button>
              {isOpen ? (
                <ul className='support-sidebar__items'>
                  {category.articles.map((article) => {
                    const isActive = category.id === activeCategory && article.slug === activeSlug
                    return (
                      <li key={article.slug}>
                        <Link
                          href={`/support/${category.id}/${article.slug}`}
                          className={isActive ? 'is-active' : undefined}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          {article.title}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              ) : null}
            </div>
          )
        })}
      </nav>
    </aside>
  )
}